import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Circle, Copy, PaperPlaneTilt, LockSimpleOpen } from "@phosphor-icons/react";

const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : null);
const OPENED = ["opened", "clicked"];
const DELIVERED = ["delivered", ...OPENED];

function downloadedLabel(project) {
  const released = (project.deliveries || []).filter((d) => d.released);
  const got = new Set((project.delivery_tracking?.downloads || []).map((x) => x.delivery_id));
  const n = released.filter((d) => got.has(d.delivery_id)).length;
  return released.length > 1 ? `Downloaded ${n} of ${released.length}` : "Downloaded";
}

function FileReceipts({ files, downloads }) {
  if (!files.length) return null;
  return (
    <div className="rounded-md border border-slate-200 p-3" data-testid="delivery-file-receipts">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Files</div>
      {files.map((f) => {
        const hits = downloads.filter((x) => x.delivery_id === f.delivery_id);
        const last = hits[hits.length - 1];
        return (
          <div key={f.delivery_id} className="flex items-start justify-between gap-3 py-1.5 border-t border-slate-100 first:border-0" data-testid={`file-receipt-${f.delivery_id}`}>
            <div className="min-w-0">
              <div className="text-sm text-slate-900 truncate">{f.filename}</div>
              <div className="text-xs text-slate-500">{f.released ? (last ? `Last downloaded ${fmtDT(last.at)}` : "Waiting for client") : "Locked until payment"}</div>
            </div>
            <span className={`badge-pill shrink-0 ${hits.length ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>
              {hits.length ? `Downloaded ${hits.length}×` : "Not downloaded"}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function reminderLine(t, downloads, status) {
  if (!t.last_sent_at) return null;
  const sent = [t.reminder_48h_sent_at && `48-hour reminder sent ${fmtDT(t.reminder_48h_sent_at)}`,
    t.reminder_5d_sent_at && `5-day reminder sent ${fmtDT(t.reminder_5d_sent_at)}`].filter(Boolean);
  if (downloads.length || t.confirmed_received_at) return sent.length ? `${sent.join(" · ")} · reminders stopped` : "No reminders needed";
  if (t.reminder_5d_sent_at) return sent.join(" · ");
  const base = new Date(t.first_sent_at || t.last_sent_at).getTime();
  if (status === "cancelled" || Date.now() - base > 14 * 24 * 3600e3) return sent.length ? sent.join(" · ") : "No automatic reminders (older than 14 days)";
  const next = t.reminder_48h_sent_at ? base + 120 * 3600e3 : base + 48 * 3600e3;
  return [...sent, `Next reminder ${fmtDT(new Date(next).toISOString())} if nothing is downloaded`].join(" · ");
}

export function DeliveryBadge({ project }) {
  const deliveries = project.deliveries || [];
  if (!deliveries.length) return null;
  const t = project.delivery_tracking || {};
  if (project.awaiting_payment_release) {
    return <span className="badge-pill bg-amber-50 text-amber-800 border-amber-200 mt-1" data-testid={`delivery-badge-${project.project_id}`}>Held · awaiting payment</span>;
  }
  const label = t.confirmed_received_at ? "Client confirmed"
    : (t.downloads || []).length ? downloadedLabel(project)
    : (t.first_opened_at || OPENED.includes(t.email_status)) ? "Email opened"
    : "Released";
  return <span className="badge-pill bg-emerald-50 text-emerald-800 border-emerald-200 mt-1" data-testid={`delivery-badge-${project.project_id}`}>{label}</span>;
}

function Step({ done, label, when, testid }) {
  return (
    <div className="flex items-start gap-2.5 py-1.5" data-testid={testid}>
      {done ? <CheckCircle size={18} weight="fill" className="text-emerald-600 mt-0.5" /> : <Circle size={18} className="text-slate-300 mt-0.5" />}
      <div>
        <div className={`text-sm ${done ? "text-slate-900 font-medium" : "text-slate-400"}`}>{label}</div>
        {when && <div className="text-xs text-slate-500">{when}</div>}
      </div>
    </div>
  );
}

export function DeliveryTrackingDialog({ project, clientEmail, onClose }) {
  const qc = useQueryClient();
  const [to, setTo] = useState("");
  useEffect(() => { setTo(clientEmail || ""); }, [clientEmail, project?.project_id]);
  const pid = project?.project_id;
  const { data, isLoading } = useQuery({
    queryKey: ["delivery-status", pid], enabled: !!pid,
    queryFn: async () => (await api.get(`/projects/${pid}/delivery-status`)).data,
  });
  const refresh = () => { qc.invalidateQueries({ queryKey: ["delivery-status", pid] }); qc.invalidateQueries({ queryKey: ["projects"] }); };
  const resend = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/resend-translation`, { to: to || null })).data,
    onSuccess: (d) => { toast.success(`Download link sent to ${d.sent_to}`); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Resend failed"),
  });
  const release = useMutation({
    mutationFn: async () => (await api.post(`/projects/${pid}/release-translation`)).data,
    onSuccess: () => { toast.success("Released — download link emailed to client"); refresh(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Release failed"),
  });

  const t = data?.tracking || {};
  const released = (data?.deliveries || []).filter((d) => d.released);
  const downloads = t.downloads || [];
  const opened = t.first_opened_at || OPENED.includes(t.email_status) || downloads.length > 0 || !!t.confirmed_received_at;
  const inbox = DELIVERED.includes(t.email_status) || !!opened;
  return (
    <Dialog open={!!project} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-lg" data-testid="delivery-tracking-dialog">
        <DialogHeader><DialogTitle>Delivery tracking — {project?.title}</DialogTitle></DialogHeader>
        {isLoading || !data ? <div className="text-sm text-slate-400">Loading…</div> : (
          <div className="space-y-4 text-sm">
            <div className="rounded-md border border-slate-200 p-3">
              <Step testid="step-released" done={released.length > 0} label={released.length ? `Released after payment (${released.length} file${released.length > 1 ? "s" : ""})` : "Held — waiting for payment"} when={fmtDT(released[0]?.released_at)} />
              <Step testid="step-sent" done={!!t.last_sent_at} label={t.last_sent_at ? `Download link emailed to ${t.last_sent_to}${t.send_count > 1 ? ` · sent ${t.send_count}×` : ""}` : "Download link not sent yet"} when={fmtDT(t.last_sent_at)} />
              <Step testid="step-delivered" done={inbox} label={t.email_status === "bounced" ? "Email bounced — try another address" : inbox ? "Reached their inbox" : "Not yet confirmed in their inbox"} when={t.email_status ? `Status: ${t.email_status}` : null} />
              <Step testid="step-opened" done={!!opened} label={opened ? `Email opened${t.open_count > 1 ? ` · ${t.open_count}×` : ""}` : "Email not opened yet"} when={fmtDT(t.first_opened_at)} />
              <Step testid="step-downloaded" done={downloads.length > 0} label={downloads.length ? `Downloaded ${downloads.length}×` : "Not downloaded yet"} when={downloads.length ? `Last: ${fmtDT(downloads[downloads.length - 1].at)}` : null} />
              <Step testid="step-confirmed" done={!!t.confirmed_received_at} label={t.confirmed_received_at ? `Client clicked "I received it"${t.confirmed_name ? ` — ${t.confirmed_name}` : ""}` : "Client hasn't confirmed receipt"} when={fmtDT(t.confirmed_received_at)} />
            </div>
            <FileReceipts files={data.deliveries || []} downloads={downloads} />
            {reminderLine(t, downloads, project?.status) && (
              <div className="text-xs text-slate-600 px-1" data-testid="delivery-reminder-status">{reminderLine(t, downloads, project?.status)}</div>
            )}
            {data.awaiting_payment_release ? (
              <Button onClick={() => window.confirm("Release the clean file now? Use this when the client paid by Zelle, cash or check.") && release.mutate()}
                disabled={release.isPending} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="delivery-release-now-btn">
                <LockSimpleOpen size={16} className="mr-1.5" /> {release.isPending ? "Releasing…" : "Release now (paid another way)"}
              </Button>
            ) : (
              <div className="space-y-2">
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Resend download link to</div>
                <div className="flex gap-2">
                  <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="delivery-resend-email" />
                  <Button onClick={() => resend.mutate()} disabled={resend.isPending || !to} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="delivery-resend-btn">
                    <PaperPlaneTilt size={16} className="mr-1.5" /> {resend.isPending ? "Sending…" : "Resend"}
                  </Button>
                </div>
                {data.download_link && (
                  <Button variant="outline" className="w-full" data-testid="delivery-copy-link-btn"
                    onClick={() => { navigator.clipboard.writeText(data.download_link); toast.success("Link copied — share it by text or WhatsApp"); }}>
                    <Copy size={16} className="mr-1.5" /> Copy download link
                  </Button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
